import { useMemo } from 'react';

import {
  TaxonomyGetResponse,
  CreateCompleteVersionDto,
} from '@/services/TaxonomiesService';

interface UseDisplayedTaxonomiesProps {
  taxonomies: TaxonomyGetResponse[];
  taxonomiesUpdated: CreateCompleteVersionDto;
  selectedCategory: string;
  group: string | null;
  groupName: string | null;
  property: string | null;
}

export function useDisplayedTaxonomies({
  taxonomies,
  taxonomiesUpdated,
  selectedCategory,
  group,
  groupName,
  property,
}: UseDisplayedTaxonomiesProps) {
  const displayedItems = useMemo(() => {
    const addedForThisCategory = taxonomiesUpdated.data.filter(
      (item) => item.categoryName === selectedCategory,
    );

    const mergedTaxonomies = [
      ...taxonomies.filter((originalItem) => {
        const existsUpdated = addedForThisCategory.some(
          (updatedItem) =>
            updatedItem.type === originalItem.type &&
            updatedItem.groupName === originalItem.groupName &&
            updatedItem.property === originalItem.property &&
            updatedItem.categoryName === originalItem.categoryName,
        );
        return !existsUpdated;
      }),
      ...addedForThisCategory,
    ];

    const filtered = mergedTaxonomies.filter((item) => {
      const isRemoved =
        item.id && taxonomiesUpdated.taxonomiesToRemove.includes(item.id);

      return (
        !isRemoved &&
        (!group || item.type === group) &&
        (!groupName || item.groupName === groupName) &&
        (!property || item.property === property)
      );
    });

    return filtered;
  }, [
    taxonomies,
    taxonomiesUpdated,
    selectedCategory,
    group,
    groupName,
    property,
  ]);

  const uniqueValues = useMemo(() => {
    return {
      groups: Array.from(new Set(displayedItems.map((item) => item.type))),
      groupNames: Array.from(
        new Set(displayedItems.map((item) => item.groupName)),
      ),
      properties: Array.from(
        new Set(displayedItems.map((item) => item.property)),
      ),
    };
  }, [displayedItems]);

  return { displayedItems, uniqueValues };
}
